/* hs-eslint ignored failing-rules */
// cribbed from calling-remote-app/static/js/navigation/hooks/useCallingSettings.ts
import { useEffect, useState, useMemo } from 'react';
import { fetchCallTypes } from 'calling-lifecycle-internal/call-types/clients/fetchCallTypes';
import { fetchCallsV1Settings } from 'calling-internal-common/calls-v1-settings/clients/fetchCallsV1Settings';
import { fetchCallingAppealState } from 'calling-lifecycle-internal/calling-appeals/clients/fetchCallingAppealState';
import { fetchUserSettingsAPI } from 'calling-lifecycle-internal/user-settings/clients/userSettingsClient';
import memoizeWithTimeout from '../../utils/memoize';
import { fetchCallsV1SettingsUsage } from 'calling-lifecycle-internal/calls-v1-settings-usage/client/fetchCallsV1SettingsUsage';
import { getCallReceivingPreference } from 'calling-internal-common/calling-orchestration-v1/settings/user/clients/UserSettingsApi';
import { flattenFromNumber } from 'calling-internal-common/calls-v1-settings/records/InitialLoadSettingsRecords/FromNumber';
import { PhoneNumberSources } from 'calling-internal-common/constants/PhoneNumberSources';
import { updateCallSettings, updateWidgetDetails } from 'worker-connection-library/hooks/useWidgetDetails';
import { updateProviderDetails } from 'worker-connection-library/hooks/useProviderDetails';
import { getHubInfo } from 'calling-lifecycle-internal/hubs-v1-hubs/clients/getHubInfo';
import PortalIdParser from 'PortalIdParser';
import { getCallingLimits } from 'calling-internal-common/utils/getCallingLimits';
import { FetchUserSettingsDefaultValues } from '../constants/UserSettingsDefaultValues';
import { getInstalledCallingSettings } from '../utils/getInstalledCallingSettings';
import { hasPaidHub } from 'calling-orchestration-shared-library/hub-info/utils/hasCallingFeature';
import { hasCallingMinutesPerPortal } from 'calling-orchestration-shared-library/hub-info/utils/getCallingLimits';
let memoizedFetchCallsV1Settings = memoizeWithTimeout(() => {
  return fetchCallsV1Settings();
}, 86400);
let memoizedFetchCallingAppealState = memoizeWithTimeout(() => {
  return fetchCallingAppealState();
}, 86400);
let memoizedFetchCallsV1SettingsUsage = memoizeWithTimeout(() => {
  return fetchCallsV1SettingsUsage();
}, 86400);
let memoizedFetchCallReceivingPreference = memoizeWithTimeout(() => {
  return getCallReceivingPreference();
}, 86400);
let memoizedGetHubInfo = memoizeWithTimeout(portalId => {
  return getHubInfo(portalId);
}, 86400);
let memoizedGetCallTypes = memoizeWithTimeout(() => {
  return fetchCallTypes();
});
let memoizedGetDefaultTaskSettings = memoizeWithTimeout(defaultValues => {
  return fetchUserSettingsAPI(defaultValues);
});
export const useCallingSettingsFetch = props => {
  const [connectFromNumbers, setConnectFromNumbers] = useState();
  const [externalFromNumbers, setExternalFromNumbers] = useState();
  const [portalFromNumbers, setPortalFromNumbers] = useState();
  const hasFirstPartyNumbers = useMemo(() => {
    if (!connectFromNumbers || !externalFromNumbers || !portalFromNumbers) {
      return undefined;
    }
    return connectFromNumbers && connectFromNumbers.length > 0 || externalFromNumbers && externalFromNumbers.length > 0 || (portalFromNumbers && portalFromNumbers.length) > 0;
  }, [connectFromNumbers, externalFromNumbers, portalFromNumbers]);
  const [hasCallingAccess, setHasCallingAccess] = useState(null);
  const [callReceivingPreference, setCallReceivingPreference] = useState(null);
  const [isHubSpotCallingEnabled, setIsHubSpotCallingEnabled] = useState(null);
  const [isRecordingEnabled, setIsRecordingEnabled] = useState(null);
  const [callingLimit, setCallingLimit] = useState(null);
  const [callDispositions, setCallDispositions] = useState([]);
  const [callTypes, setCallTypes] = useState([]);
  const [defaultTaskSettings, setDefaultTaskSettings] = useState(null);
  const [installedCallingSettings, setInstalledCallingSettings] = useState([]);
  const [isCallingSettingsLoading, setIsCallingSettingsLoading] = useState(false);
  const [hasCompletedFetching, setHasCompletedFetching] = useState(false);
  const [isCallOutcomeRequired, setIsCallOutcomeRequired] = useState(null);
  const {
    shouldFetchData
  } = props;
  useEffect(() => {
    if (hasCompletedFetching) {
      const callSettings = {
        connectFromNumbers,
        externalFromNumbers,
        portalFromNumbers,
        hasCallingAccess,
        isHubSpotCallingEnabled,
        isRecordingEnabled,
        callReceivingPreference,
        callingLimit,
        callDispositions,
        callTypes,
        defaultTaskSettings,
        installedCallingSettings,
        isCallOutcomeRequired,
        isPaidHub: hasPaidHub()
      };
      updateCallSettings(callSettings).catch(() => {
        // do nothing, errors are handled in cross tab code
      });
      updateProviderDetails({
        installedCallingSettings
      }).catch(() => {
        // do nothing, errors are handled in cross tab code
      });
      setIsCallingSettingsLoading(false);
    }
  }, [connectFromNumbers, externalFromNumbers, hasCallingAccess, hasCompletedFetching, isHubSpotCallingEnabled, isRecordingEnabled, portalFromNumbers, setIsCallingSettingsLoading, callReceivingPreference, callingLimit, callDispositions, callTypes, defaultTaskSettings, installedCallingSettings, isCallOutcomeRequired]);
  useEffect(() => {
    if (!shouldFetchData) {
      return;
    }
    setIsCallingSettingsLoading(true);
    memoizedFetchCallsV1Settings().then(callsV1SettingsResponse => {
      var _callsV1SettingsRespo;
      const hubSpotCallingEnabled = callsV1SettingsResponse && callsV1SettingsResponse.callingAdminSettings && callsV1SettingsResponse.callingAdminSettings.result && callsV1SettingsResponse.callingAdminSettings.result.hubSpotCallingEnabled;
      // TODO Fix: https://hubspot.slack.com/archives/C016LJXTK35/p1668619337288809
      setIsHubSpotCallingEnabled(hubSpotCallingEnabled === null ? true : hubSpotCallingEnabled);
      const _connectFromNumbers = callsV1SettingsResponse && callsV1SettingsResponse.initialLoadSettings && callsV1SettingsResponse.initialLoadSettings.connectNumbers && callsV1SettingsResponse.initialLoadSettings.connectNumbers.map(connectNumber => {
        connectNumber.source = PhoneNumberSources.CONNECT_NUMBER;
        return flattenFromNumber(connectNumber);
      });
      const _hasConnectFromNumbers = _connectFromNumbers ? _connectFromNumbers.length > 0 : false;
      setConnectFromNumbers(_connectFromNumbers);
      const _externalFromNumbers = callsV1SettingsResponse && callsV1SettingsResponse.initialLoadSettings && callsV1SettingsResponse.initialLoadSettings.fromNumbers && callsV1SettingsResponse.initialLoadSettings.fromNumbers.map(externalFromNumber => {
        externalFromNumber.source = PhoneNumberSources.EXTERNAL_NUMBER;
        return flattenFromNumber(externalFromNumber);
      });
      setExternalFromNumbers(_externalFromNumbers);
      const _portalFromNumbers = callsV1SettingsResponse && callsV1SettingsResponse.initialLoadSettings && callsV1SettingsResponse.initialLoadSettings.portalFromNumbers && callsV1SettingsResponse.initialLoadSettings.portalFromNumbers.map(portalFromNumber => {
        portalFromNumber.source = PhoneNumberSources.HUBSPOT_NUMBER;
        return flattenFromNumber(portalFromNumber);
      });
      setPortalFromNumbers(_portalFromNumbers);
      const recordingEnabled = callsV1SettingsResponse && callsV1SettingsResponse.initialLoadSettings && callsV1SettingsResponse.initialLoadSettings.recordingEnabled;
      setIsRecordingEnabled(recordingEnabled);
      const dispositions = callsV1SettingsResponse && callsV1SettingsResponse.initialLoadSettings && ((_callsV1SettingsRespo = callsV1SettingsResponse.initialLoadSettings.callDispositions) === null || _callsV1SettingsRespo === void 0 ? void 0 : _callsV1SettingsRespo.map(disposition => {
        return {
          label: disposition.label,
          uid: disposition.id
        };
      }));
      setCallDispositions(dispositions);
      const installedCallingApplications = getInstalledCallingSettings({
        installedCallingSettings: callsV1SettingsResponse.installedCallingSettings,
        hasTwilioConnect: _hasConnectFromNumbers
      });
      setInstalledCallingSettings(installedCallingApplications);
      const callOutcomeRequired = callsV1SettingsResponse && callsV1SettingsResponse.callingAdminSettings && callsV1SettingsResponse.callingAdminSettings.result && callsV1SettingsResponse.callingAdminSettings.result.callOutcomeRequired;
      setIsCallOutcomeRequired(callOutcomeRequired);
    }).catch(() => {
      setIsCallingSettingsLoading(false);
    });
  }, [shouldFetchData, setIsCallingSettingsLoading]);
  useEffect(() => {
    if (!shouldFetchData || isHubSpotCallingEnabled === null) {
      return;
    }
    if (isHubSpotCallingEnabled === false || hasFirstPartyNumbers === false) {
      setHasCompletedFetching(true);
      return;
    }
    Promise.all([memoizedFetchCallingAppealState(), memoizedFetchCallsV1SettingsUsage(), memoizedFetchCallReceivingPreference(), memoizedGetHubInfo(PortalIdParser.get()), memoizedGetCallTypes(), memoizedGetDefaultTaskSettings(FetchUserSettingsDefaultValues)]).then(([appealStateResponse, callsV1SettingsUsageResponse, userSettingsResponse, hubInfoResponse, callTypesResponse, defaultTaskSettingsResponse]) => {
      const hasCallingAccessResp = !appealStateResponse.suspended && hasCallingMinutesPerPortal();

      // todo, we should move callV1SettingsUsage to call settings
      updateWidgetDetails({
        callsV1SettingsUsage: callsV1SettingsUsageResponse
      }).catch(() => {
        // do nothing, errors are handled in cross tab code
      });
      const _callReceivingPreference = userSettingsResponse.callReceivingPreference;
      const scopes = hubInfoResponse.scopeLimitInfo.scopes;
      const callingLimitType = getCallingLimits(scopes).type;
      const types = callTypesResponse.map(type => {
        return {
          value: type.name
        };
      });
      setHasCallingAccess(hasCallingAccessResp);
      setCallReceivingPreference(_callReceivingPreference);
      setCallingLimit(callingLimitType);
      setCallTypes(types);
      setDefaultTaskSettings(defaultTaskSettingsResponse);
      setHasCompletedFetching(true);
    }).catch(() => {
      setIsCallingSettingsLoading(false);
    });
  }, [shouldFetchData, isHubSpotCallingEnabled, setIsCallingSettingsLoading, setCallingLimit, hasFirstPartyNumbers]);
  return {
    isCallingSettingsLoading
  };
};
export const clearMemoizedFetchForTesting = () => {
  memoizedFetchCallsV1Settings = fetchCallsV1Settings;
  memoizedFetchCallingAppealState = fetchCallingAppealState;
  memoizedFetchCallsV1SettingsUsage = fetchCallsV1SettingsUsage;
  memoizedFetchCallReceivingPreference = getCallReceivingPreference;
  memoizedGetHubInfo = getHubInfo;
  memoizedGetCallTypes = fetchCallTypes;
  memoizedGetDefaultTaskSettings = fetchUserSettingsAPI;
};